import { convertTOKM } from '../../Customer/CrossMediaModule/Components/Common';
import { allCurrencyCodes } from '../SDMMM/CurrencyCodes.js';

// f(x) of the diminishing returns function
export function f(x, p, roas_function) {
  if (roas_function === 'hill') {
    return (p.beta / (1 + (x / p.mu_x / p.ec) ** -p.slope)) * p.mu_y;
  } else if (roas_function === 'ax_b') {
    return p.a * (x / p.mu_x) ** p.b * p.mu_y;
  }
}

// Calculate single value marginal return
export function calculateSingleValueMROAS(spend, p, roas_function) {
  let y1 = f(spend, p, roas_function);
  let y2 = f(spend + 1, p, roas_function);
  return y2 - y1;
}

// Calculate average MROAS
export function calculateMROAS(spend_list, p, roas_function) {
  let average_spent = 0;
  let mrg = 0;
  if (spend_list?.length > 0) {
    // if spending occurred, compute marginal return
    average_spent = spend_list.reduce((a, b) => a + b) / spend_list.length;

    // remove spent < 10% of average
    let filtered_average = spend_list.filter((x) => x >= average_spent * 0.1);
    average_spent =
      filtered_average.reduce((a, b) => a + b) / filtered_average.length;

    mrg = calculateSingleValueMROAS(average_spent, p, roas_function);
  }
  return [mrg, average_spent];
}

//Calculate TotalROI
export function calculateTotalROI(target, p, roas_function) {
  let curr_x;
  if (target === undefined) {
    curr_x = 1;
  } else {
    curr_x = parseInt(target.replace(/\$|,/g, ''));
  }

  return f(curr_x, p, roas_function);
}

export const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD', // CAD adds a 'CA$2,500'
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const shortFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD', // 'CAD' adds a 'CA$2,500', so we use 'USD' to get '$2,500'
  maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
});

export const formatterVolume = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const shortFormatterVolume = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export function shortFormatConverter(number, symbol = '', using = '') {
  const sign = number < 0 ? '-' : '';
  number = Math.abs(number);
  return using === 'TOKM'
    ? `${sign}${symbol}${convertTOKM(number, 2)}`
    : `${sign}${symbol}${shortFormatterVolume.format(number)}`;
}

export function formatConverter(number, symbol = '') {
  const sign = number < 0 ? '-' : '';
  number = Math.abs(number);
  return `${sign}${symbol}${formatterVolume.format(number)}`;
}

export function getDigits(num) {
  // assumes num>0
  return Math.floor(num).toString().length;
}

/**
 * Rounds a numeric value to two decimal places.
 *
 * @param {number} num - The value to round.
 * @returns {number} The value rounded to two decimal places.
 */
export function roundToTwoDecimals(num) {
  return Math.round(num * 100) / 100;
}

/**
 * Computes Return on Ad Spend (ROAS).
 *
 * Both inputs (contribution and spending) are each rounded to two decimal
 * places before the division. If the rounded spending is not greater than 0,
 * the function safely returns 0.
 *
 * @param {number} contribution - Contribution attributed to the media spend.
 * @param {number} spending - The media spend.
 * @returns {number} The computed ROAS.
 */
export function computeROAS(contribution, spending) {
  const roundedContribution = roundToTwoDecimals(contribution);
  const roundedSpending = roundToTwoDecimals(spending);
  return roundedSpending > 0 ? roundedContribution / roundedSpending : 0;
}

/**
 * Converts a given value representing ROAS to an equivalent CPA value.
 * If the input number is zero, it returns zero to avoid division by zero.
 * Otherwise, it returns the reciprocal of the input number.
 *
 * ROAS = contribution / spend
 * CPA = spend / contribution
 *
 * Hence, CPA === 1 / ROAS
 *
 * @param {number} num - The input number representing ROAS.
 * @returns {number} The converted CPA value, or 0 if num is 0.
 */
export function applyCostPerAcquisitionConversion(num) {
  return num === 0 ? 0 : 1 / num;
}

export function convertCurrencyHistorical(data, currency, reverse = false) {
  if (!Array.isArray(data) || Object.keys(currency).length === 0) return [];
  return data.map(
    (value, index) =>
      value *
      (!reverse ? currency.historical[index] : 1 / currency.historical[index])
  );
}

export function convertCurrencyAverage(data, currency, reverse = false) {
  if (!Array.isArray(data) || Object.keys(currency).length === 0) return [];
  return data.map(
    (value) => value * (!reverse ? currency.average : 1 / currency.average)
  );
}

/**
 * @typedef CurrencyCode
 * @type {Object}
 * @property {string} name - currency name for display
 * @property {string} symbol - currency symbol representation
 * @property {string} code - currency conversion code
 */

/**
 *
 * @param {string} countryCode Country code consisting of two letters. Typically 'ca' or 'us'
 * @returns {CurrencyCode} Defaults to USD
 */
export const getDefaultCurrencyByCountryCode = (countryCode) => {
  switch (countryCode) {
    case 'ca':
      return allCurrencyCodes[0]; // CAD
    case 'us':
      return allCurrencyCodes[1]; // USD
    default:
      return allCurrencyCodes[1]; // USD
  }
};

export function findColLength(list) {
  for (let i = list?.length - 1; i >= 0; i--) {
    if (list[i] !== '' && list[i] !== null) {
      return i + 1;
    }
  }

  return 0;
}

export function skipIndices(array, indexes) {
  let return_array = [];
  for (const [index, value] of array.entries()) {
    if (!indexes.includes(index)) {
      return_array.push(value);
    }
  }
  return return_array;
}

/**
 * @param {Object} obj - The object whose size will be calculated.
 * @returns {number} The size of the JSON-encoded object in bytes.
 */
export const getObjectSizeInBytes = (obj) => {
  // Convert your object to a JSON string
  const jsonString = JSON.stringify(obj);
  // Encode the string as UTF-8 and return the length
  return new TextEncoder().encode(jsonString).length;
};
